import { useEffect, useRef } from "react";
import TileLayer from "ol/layer/Tile";
import { XYZ } from "ol/source";
import { isSafeHistoricTileUrl } from "./mapWaypoints";

export { isSafeHistoricTileUrl };

export const useHistoricMapLayer = ({ map, historicOverlay, opacity = 1.0 }) => {
  const layerRef = useRef(null);

  useEffect(() => {
    const isSafe = isSafeHistoricTileUrl(historicOverlay?.url);
    if (!map || !historicOverlay?.enabled || !isSafe) {
      if (layerRef.current && map) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
      return;
    }

    const effectiveOpacity = typeof opacity === "number" ? Math.max(0.005, opacity) : 1.0;

    if (!layerRef.current) {
      const source = new XYZ({
        url: historicOverlay.url.trim(),
        attributions: historicOverlay.attribution || historicOverlay.name || "",
        crossOrigin: "anonymous",
        transition: 0,
      });
      const layer = new TileLayer({
        className: "ol-layer scrolly-historic-overlay-layer",
        source,
        opacity: effectiveOpacity,
        preload: Infinity,
        zIndex: 5,
      });

      const resolvedFilter = historicOverlay?.filter || "none";
      if (typeof layer.on === "function") {
        layer.on("prerender", (evt) => {
          if (evt?.context?.canvas) {
            evt.context.canvas.style.filter = resolvedFilter;
          }
        });
        layer.on("postrender", (evt) => {
          if (evt?.context?.canvas && resolvedFilter === "none") {
            evt.context.canvas.style.filter = "none";
          }
        });
      }

      layerRef.current = layer;
      map.addLayer(layer);
    } else {
      const resolvedFilter = historicOverlay?.filter || "none";
      if (typeof layerRef.current.on === "function") {
        layerRef.current.on("prerender", (evt) => {
          if (evt?.context?.canvas) {
            evt.context.canvas.style.filter = resolvedFilter;
          }
        });
      }
    }

    return () => {
      if (layerRef.current && map) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [
    map,
    historicOverlay?.enabled,
    historicOverlay?.url,
    historicOverlay?.filter,
    historicOverlay?.attribution,
    historicOverlay?.name,
  ]);

  useEffect(() => {
    if (layerRef.current && typeof opacity === "number") {
      const effectiveOpacity = Math.max(0.005, opacity);
      layerRef.current.setOpacity(effectiveOpacity);
    }
  }, [opacity]);

  return layerRef.current;
};
